<script lang="ts" setup>
import { onMounted, type Ref } from "vue";
import H2Heading from "~/components/H2Heading.vue";
import { useFetch } from "#app";
import type StatamicCollectionRequest from "~~/types/requests/StatamicCollectionRequest";

// Properties

// Data
const config = useRuntimeConfig();
const staff: Ref<
    {
        id: number;
        name: string;
        role: string;
        photo: object;
        staff_photo: string;
        staff_photo_2x: string;
    }[]
> = ref([]);

// Functions
async function fetchStaff() {
    const requestURL = config.public.STATAMIC_API + `/collections/staff/entries?&limit=45`;

    const { data } = await useFetch<StatamicCollectionRequest>(requestURL, {
        method: "GET",
    });

    staff.value = data.value.data;
}

await fetchStaff();

onMounted(async () => {
    // swiper element
    const swiperEl = document.querySelector("#our-staff-slides");
    const btnNext = document.querySelector("#our-staff-slides-btn-next");
    const btnPrev = document.querySelector("#our-staff-slides-btn-prev");

    // swiper parameters
    const swiperParams = {
        //slidesPerView: 4,
        spaceBetween: 15,
        loop: false,
        navigation: {
            nextEl: btnNext,
            prevEl: btnPrev,
        },
        autoplay: {
            delay: 8000,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
        },
        breakpoints: {
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 5 },
        },
        on: {
            init() {
                // ...
            },
        },
    };

    // now we need to assign all parameters to Swiper element
    Object.assign(swiperEl, swiperParams);

    // and now initialize it
    swiperEl?.initialize();
});
</script>

<template>
    <div>
        <H2Heading
            size="text-2xl md:text-3xl"
            class="mb-8 text-center"
            >Our Staff</H2Heading
        >
        <div class="relative pb-6">
            <swiper-container
                id="our-staff-slides"
                init="false"
                class="swiper-container swiper-initialized swiper-horizontal px-2 md:w-[70%] lg:w-[80%] xl:w-[87%]"
            >
                <swiper-slide
                    v-for="person in staff"
                    :key="`staff-${person.id}`"
                    class="overflow-hidden text-center"
                >
                    <div
                        class="mx-auto h-32 w-32 overflow-hidden"
                        :class="
                            !person.photo || person.photo.url.toLowerCase().includes('portrait_placeholder')
                                ? 'rounded-full'
                                : ''
                        "
                    >
                        <img
                            :src="person.staff_photo ? person.staff_photo : '/images/home/Portrait_Placeholder.png'"
                            :srcset="`${person.staff_photo} 1x, ${person.staff_photo_2x} 2x`"
                            :alt="'Portrait of ' + person.name"
                            class="mx-auto h-full"
                        />
                    </div>
                    <div class="mt-4 font-semibold">{{ person.name }}</div>
                    <div class="text-abc-red">{{ person.role }}</div>
                    <div class="mt-1 flex justify-center">
                        <a
                            v-if="person.phone"
                            :href="`tel: ${person.phone}`"
                        >
                            <img
                                src="/images/icons/phone-regular.svg"
                                class="w-5"
                            />
                        </a>
                        <a
                            v-if="person.email"
                            :href="`mailto: ${person.email}`"
                            class="ml-3"
                        >
                            <img
                                src="/images/icons/envelope-regular.svg"
                                class="w-5"
                            />
                        </a>
                    </div>
                </swiper-slide>
            </swiper-container>

            <div
                class="absolute top-0 right-0 z-10 hidden h-full items-center justify-center border-l-0 bg-white p-4 shadow-xl shadow-white md:flex"
            >
                <div
                    id="our-staff-slides-btn-next"
                    class="flex-none cursor-pointer rounded-full border px-5 py-3 shadow-lg hover:border-2"
                >
                    <img
                        src="/images/fa/chevron-right-regular.svg"
                        class="w-5"
                    />
                </div>
            </div>
            <div
                class="absolute top-0 left-0 z-10 hidden h-full items-center justify-center border-r-0 bg-white p-4 shadow-xl shadow-white md:flex"
            >
                <div
                    id="our-staff-slides-btn-prev"
                    class="flex-none cursor-pointer rounded-full border px-5 py-3 shadow-lg hover:border-2"
                >
                    <img
                        src="/images/fa/chevron-left-regular.svg"
                        class="w-5"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
